import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, ArrowUp, ArrowDown, Mail, HelpCircle, LogOut } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useUser } from '../context/UserContext';
import { useGlobalFontSize } from '../context/GlobalFontSizeContext';
import { trackLogout } from '../utils/analytics';
import { getLogoutRedirect } from '../utils/logoutRedirect';

const USER_MENU_WIDTH = 267;
const USER_MENU_LEFT = 20;
const USER_MENU_GAP = 8;
// Breathing room so a tall panel cannot run into the top of the viewport.
const USER_MENU_VIEWPORT_MARGIN = 8;
// Never collapse to nothing if the trigger sits very close to the top; scrolling
// a short panel beats rendering a sliver.
const USER_MENU_MIN_HEIGHT = 120;

const SidebarUserMenu = ({ isCollapsed }) => {
  const { userDetails, tier } = useUser();
  const { t } = useTranslation();
  const { fontSize, increase, decrease } = useGlobalFontSize();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ bottom: 0, maxHeight: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const displayName = userDetails
    ? `${userDetails.name} ${userDetails.surname}`
    : t('common.user');

  const planLabel = useMemo(() => {
    if (!tier || tier === 'free') {
      return t('billing.freePlan');
    }
    const tierName = tier === 'ifpa' ? 'IFPA' : tier.charAt(0).toUpperCase() + tier.slice(1);
    return t('usage.planName', { plan: tierName });
  }, [tier, t]);

  const isMinSize = fontSize <= 8;
  const isMaxSize = fontSize >= 24;

  const fontButtonClass = `
    flex items-center justify-center gap-[2px] cursor-pointer
    h-[36px] w-[36px] rounded-[8px]
    bg-white dark:bg-[#282A29] border border-[#C3C4C3] dark:border-[#5d625f]
    text-[#282A29] dark:text-[#f0f5f2] font-medium
    transition-colors hover:bg-[#F0F4F1] dark:hover:bg-[#1d1d1d]
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  /**
   * Anchor the panel by its BOTTOM edge to the trigger.
   *
   * This used to subtract a hard-coded 234px panel height from the trigger's top
   * and pin the panel to that height. Every row inside scales with the
   * letter-size control, so at the top of the range the content outgrew 234px
   * and `overflow-hidden` silently cut the "Log out" row off. Anchoring the
   * bottom lets the height follow its content; `maxHeight` only bites when there
   * genuinely isn't room above the trigger, and it scrolls instead of clipping.
   */
  const updateMenuPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPosition({
      bottom: Math.round(window.innerHeight - rect.top + USER_MENU_GAP),
      maxHeight: Math.max(
        USER_MENU_MIN_HEIGHT,
        Math.round(rect.top - USER_MENU_GAP - USER_MENU_VIEWPORT_MARGIN)
      ),
    });
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
  }, [isCollapsed]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      trackLogout();
      await signOut(auth);
      setIsOpen(false);
      navigate(getLogoutRedirect());
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const actionItemClass =
    'flex cursor-pointer items-center gap-[12px] px-[16px] py-[10px] text-[#444746] dark:text-[#f0f5f2] hover:bg-[#F0F4F1] dark:hover:bg-[#282a29]/50 transition-colors';

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (!isOpen) {
            updateMenuPosition();
          }
          setIsOpen((prev) => !prev);
        }}
        className={`w-full flex cursor-pointer items-center gap-[8px] rounded-[4px] hover:bg-[#E8EDEA] dark:hover:bg-[#282a29]/50 transition-colors py-[4px] ${
          isCollapsed ? 'justify-center' : ''
        }`}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <div className="h-[32px] w-[32px] rounded-full bg-white dark:bg-white flex items-center justify-center flex-shrink-0">
          <User size={20} className="text-[#282A29] dark:text-[#1d1d1d]" />
        </div>
        {!isCollapsed && (
          <span
            className="font-['Inter'] font-medium text-[#444746] dark:text-[#f0f5f2] truncate text-left"
            style={{ fontSize }}
          >
            {displayName}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="fixed z-[200] flex flex-col bg-[#F9F9F9] dark:bg-[#282a29] border border-[#C3C4C3] dark:border-[#5d625f] rounded-[8px] shadow-xl overflow-y-auto overflow-x-hidden"
          style={{
            left: `${USER_MENU_LEFT}px`,
            bottom: `${menuPosition.bottom}px`,
            width: `${USER_MENU_WIDTH}px`,
            maxHeight: `${menuPosition.maxHeight}px`,
          }}
          role="menu"
          aria-label="User menu"
        >
          <div className="flex items-center gap-[12px] px-[16px] py-[12px]">
            <div className="h-[40px] w-[40px] rounded-full bg-white dark:bg-white flex items-center justify-center flex-shrink-0">
              <User size={24} className="text-[#282A29] dark:text-[#1d1d1d]" />
            </div>
            <div className="min-w-0">
              <p
                className="font-['Inter'] font-semibold text-[#282A29] dark:text-[#f0f5f2] truncate"
                style={{ fontSize }}
              >
                {displayName}
              </p>
              <p className="font-['Inter'] text-bloom-green text-[14px]">{planLabel}</p>
            </div>
          </div>

          <div className="border-t border-[#C3C4C3] dark:border-[#5d625f]" />

          <div className="flex items-center gap-[8px] px-[16px] py-[10px]">
            <button
              type="button"
              onClick={decrease}
              disabled={isMinSize}
              className={fontButtonClass}
              aria-label="Decrease font size"
            >
              <span className="text-[14px]">A</span>
              <ArrowDown size={12} />
            </button>
            <button
              type="button"
              onClick={increase}
              disabled={isMaxSize}
              className={fontButtonClass}
              aria-label="Increase font size"
            >
              <span className="text-[14px]">A</span>
              <ArrowUp size={12} />
            </button>
          </div>

          <div className="border-t border-[#C3C4C3] dark:border-[#5d625f]" />

          <div className="flex flex-col">
            <Link
              to="/contact"
              onClick={() => setIsOpen(false)}
              className={actionItemClass}
              role="menuitem"
            >
              <Mail size={18} className="flex-shrink-0" />
              <span className="font-['Inter'] font-medium" style={{ fontSize }}>
                {t('nav.contact')}
              </span>
            </Link>
            <Link
              to="/faq"
              onClick={() => setIsOpen(false)}
              className={actionItemClass}
              role="menuitem"
            >
              <HelpCircle size={18} className="flex-shrink-0" />
              <span className="font-['Inter'] font-medium" style={{ fontSize }}>
                {t('nav.faq')}
              </span>
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className={`${actionItemClass} w-full text-left`}
              role="menuitem"
            >
              <LogOut size={18} className="flex-shrink-0" />
              <span className="font-['Inter'] font-medium" style={{ fontSize }}>
                {t('nav.logout')}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SidebarUserMenu;
